import { useEffect, useState } from 'react'

import { IncidentType } from '@/graphql/generated/schemas'

import { incidentTypesWithoutVideo } from '../utils/helpers'

/** Maximum time to wait for a clip before showing "Clip unavailable" */
export const CLIP_LOADER_TIMEOUT_MS = 3 * 60 * 1000 // 3 minutes

interface UseClipLoaderTimeoutIProps {
  createdAt: string
  hasVideoClip: boolean
  incidentType: IncidentType
}

interface UseClipLoaderTimeoutResultIProps {
  shouldShowWaitingState: boolean
  hasTimedOut: boolean
}

/**
 * Manages the 3-minute timeout for clip retrieval.
 *
 * Uses a single setTimeout (not interval) for efficiency - fires exactly
 * when the deadline expires, avoiding unnecessary re-renders during the
 * waiting period.
 */
export const useClipLoaderTimeout = ({
  createdAt,
  hasVideoClip,
  incidentType,
}: UseClipLoaderTimeoutIProps): UseClipLoaderTimeoutResultIProps => {
  const isExpectingVideo = !incidentTypesWithoutVideo.includes(incidentType)
  const createdAtMs = Date.parse(createdAt)
  const isValidDate = !Number.isNaN(createdAtMs)
  const deadlineMs = createdAtMs + CLIP_LOADER_TIMEOUT_MS

  const computeRemainingMs = () => Math.max(0, deadlineMs - Date.now())

  // Calculate timeout state - treat invalid dates as not timed out (waiting for valid data)
  const computeHasTimedOut = () => {
    if (!isValidDate) return false
    return Date.now() - createdAtMs >= CLIP_LOADER_TIMEOUT_MS
  }

  const [hasTimedOut, setHasTimedOut] = useState(computeHasTimedOut)

  // Re-sync hasTimedOut when createdAtMs changes (e.g., incident data loads)
  useEffect(() => {
    setHasTimedOut(computeHasTimedOut())
  }, [createdAtMs])

  // Set a single timeout to fire when the deadline expires
  useEffect(() => {
    // Skip if invalid date, already resolved, or not applicable
    if (!isValidDate || hasTimedOut || hasVideoClip || !isExpectingVideo) {
      return
    }

    const msUntilTimeout = computeRemainingMs()

    // Handle edge case where timeout already passed
    if (msUntilTimeout === 0) {
      setHasTimedOut(true)
      return
    }

    const timeoutId = setTimeout(() => {
      setHasTimedOut(true)
    }, msUntilTimeout)

    return () => clearTimeout(timeoutId)
  }, [createdAtMs, hasVideoClip, hasTimedOut, isExpectingVideo, isValidDate])

  // Determine if we should show the waiting UI
  // Don't show waiting state if date is invalid (no incident data yet)
  const shouldShowWaitingState =
    isValidDate && !hasVideoClip && isExpectingVideo && !hasTimedOut

  return {
    shouldShowWaitingState,
    hasTimedOut,
  }
}
